import { BreadcrumbItem, Col, Row } from "reactstrap"
import { Link } from "react-router-dom"

import PropTypes from "prop-types"
import React from "react";
import { UI_PERMISSION_KEY } from "pages/Roles/newFeaturesAndPermissionsData";
import useCanManageCampaign from "hooks/useCanManageCampaign";
import useClientPermissionsAccess from "hooks/useClientPermissionsAccess";
import { bsh_isAdminUserDetailsPresent } from "helpers/sf_browser_storage_helper";
import { NAME_OF_PRODUCT_TYPES, REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants";
import useFeatureFlags from "hooks/useFeatureFlags";
import { getPrimaryTextClass } from "helpers/sf_utils";
import usePlatformSettings from 'hooks/usePlatformSettings';
import CopyUrl from "components/Common/CopyUrl";
import SfNavLink from "components/Common/SfNavLink";
import { isGoalBrand } from "config";
import { isIABrand } from "config";
import { isSfproBrand } from "config";
import { useSelector } from "react-redux";
import useFeatureFlagsForAccounts from "hooks/useFeatureFlagsForAccounts";

const CampaignBreadcrumb = ({ breadcrumbItem, title, campaignId, campaign_type, customizeColumns, copyUrl }) => {

  const { current_account } = useSelector((state) => ({
    current_account: state.accounts.current_account,
  }))

  const sourceSettingEnabledFromAccountSetting = (isGoalBrand || isIABrand || isSfproBrand) && current_account?.source_settings_enabled

  const canManageCampaign = useCanManageCampaign();
  const getActivePermissions = useClientPermissionsAccess();
  const { isProfilesEnabled, isSourceSettingsEnabled, isClicksProductEnabled } = usePlatformSettings();
  const [isFeatureFlagActive] = useFeatureFlags();
  const [isFeatureFlagActiveForAccounts] = useFeatureFlagsForAccounts();
  const isManageCampaignPermission = !!campaignId ? canManageCampaign(campaignId) : !!getActivePermissions([UI_PERMISSION_KEY]).length;

  return (
    <Row>
      <Col md={12} xs="12">
        <div className="page-title-box pb-2 d-sm-flex align-items-center justify-content-between">
          <h4 className="text-capitalize text-black mb-0 font-size-18">
            <Link to="/campaigns">
              <i className={`bx bx-arrow-back ${getPrimaryTextClass()} font-size-16 me-2 bx-sm`} />
            </Link>
            {breadcrumbItem}
            {
              copyUrl && bsh_isAdminUserDetailsPresent() && <CopyUrl />
            }
          </h4>
          <div className="page-title-right">
            <ol className="breadcrumb m-0">
              <BreadcrumbItem>
                <Link to="#" className="text-black">{title}</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <Link to="#" className="text-black">{breadcrumbItem}</Link>
              </BreadcrumbItem>
            </ol>
          </div>
        </div>
      </Col>
      <Col md={12} xs={12}>
        <Row>
          <Col md="auto" xs="8">
            <ul className="nav sfnav mb-4">
              <li className="nav-item">
                {
                  campaignId === undefined ?
                    ''
                    :
                    <SfNavLink 
                      url={`/campaigns/${campaignId}/summary`}
                      tabName='Campaign Summary'
                      isURL={true}
                      position='1'
                    />
                }
              </li>
              {isManageCampaignPermission && (
                <li className="nav-item">
                  {
                    campaignId === undefined ?
                    <SfNavLink 
                      isURL={true}
                      url={`/campaigns/create`}
                      tabName='Campaign Settings'
                    /> :
                    <SfNavLink 
                      isURL={true}
                      url={`/campaigns/${campaignId}/edit`}
                      tabName='Campaign Settings'
                    />
                  }
                </li>
              )}
              {isProfilesEnabled && (
                <>
                  {campaignId === undefined ?
                    ''
                    :
                    campaign_type && <li className="nav-item">
                      <SfNavLink 
                        isURL={true}
                        url={[NAME_OF_PRODUCT_TYPES.CLICKS, NAME_OF_PRODUCT_TYPES.SYNDI_CLICKS, NAME_OF_PRODUCT_TYPES.QUOTE_FUNNELS].includes(campaign_type) ? `/campaigns/${campaignId}/adgroups` : `/campaigns/${campaignId}/profiles`}
                        tabName={campaign_type === NAME_OF_PRODUCT_TYPES.CLICKS || campaign_type === NAME_OF_PRODUCT_TYPES.SYNDI_CLICKS || campaign_type === NAME_OF_PRODUCT_TYPES.QUOTE_FUNNELS ? 'Ad Groups' : campaign_type === NAME_OF_PRODUCT_TYPES.CALLS || campaign_type === NAME_OF_PRODUCT_TYPES.LEADS ? 'Profiles' : ''}
                      />
                    </li>
                  }
                </>
              )}
              {
                isFeatureFlagActiveForAccounts(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_CAMPAIGN_FULFILLMENT_PAGE, current_account) && !!campaignId && (
                  <li className="nav-item">
                    <SfNavLink 
                      isURL={true}
                      url={`/campaigns/${campaignId}/campaign-fulfillment`}
                      tabName='Fulfillment'
                    />
                  </li>
                )
              }
              {((isSourceSettingsEnabled && sourceSettingEnabledFromAccountSetting) ||
                campaign_type === NAME_OF_PRODUCT_TYPES.CLICKS ||
                campaign_type === NAME_OF_PRODUCT_TYPES.SYNDI_CLICKS ||
                (campaign_type === NAME_OF_PRODUCT_TYPES.QUOTE_FUNNELS && bsh_isAdminUserDetailsPresent())
                || isSourceSettingsEnabled
                && isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_SOURCE_SETTINGS_FOR_LEADS_AND_CALLS)
                && bsh_isAdminUserDetailsPresent())
                && <li className="nav-item">
                  {
                    campaignId === undefined ?
                      ''
                      :
                      (campaign_type && 
                        <SfNavLink 
                          isURL={true}
                          url={(campaign_type === NAME_OF_PRODUCT_TYPES.CLICKS || campaign_type === NAME_OF_PRODUCT_TYPES.SYNDI_CLICKS || campaign_type === NAME_OF_PRODUCT_TYPES.QUOTE_FUNNELS) ? `/campaigns/${campaignId}/source-setting` : `/campaigns/${campaignId}/call-lead-source-setting`}
                          tabName='Source Settings'
                          isAdmin={campaign_type !== NAME_OF_PRODUCT_TYPES.CLICKS && campaign_type !== NAME_OF_PRODUCT_TYPES.SYNDI_CLICKS && !sourceSettingEnabledFromAccountSetting}
                          targetID="source_setting"
                        />
                      )
                  }
                </li>
              }
              {(bsh_isAdminUserDetailsPresent() && campaign_type !== NAME_OF_PRODUCT_TYPES.SYNDI_CLICKS) && <li className="nav-item">
                {
                  campaignId === undefined || !campaign_type ?
                    ''
                    :
                    (
                      campaign_type === NAME_OF_PRODUCT_TYPES.CLICKS || campaign_type === NAME_OF_PRODUCT_TYPES.QUOTE_FUNNELS ?
                        <SfNavLink 
                          isURL={true}
                          url={`/campaigns/${campaignId}/campaign-analysis`}
                          tabName='Campaign Analysis'
                          isAdmin={true}
                          targetID="campaign_analysis"
                        />
                        
                        :
                        <SfNavLink 
                          isURL={true}
                          url={`/campaigns/${campaignId}/leads-calls-campaign-analysis`}
                          tabName='Campaign Analysis'
                          isAdmin={true}
                          targetID="campaign_analysis"
                        />
                    )
                }
              </li>
              }
            </ul>
          </Col>
          <Col className="mt-2 ms-auto" md="auto" xs="4">
            {
              (customizeColumns || "")
            }
          </Col>
        </Row>
      </Col >
    </Row >
  )
}

CampaignBreadcrumb.propTypes = {
  breadcrumbItem: PropTypes.string,
  title: PropTypes.string,
  campaignId: PropTypes.string,
  manageCampaignPermission: PropTypes.bool,
  campaign_type: PropTypes.string,
  customizeColumns: PropTypes.any
}
export default CampaignBreadcrumb
